import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { PageWrapperComponent } from "../../../components/ui-kit/page-components/page-wrapper/page-wrapper.component";
import { HeavyHeaderComponent } from "../../../components/ui-kit/typography/heavy-header/heavy-header.component";
import { PrimaryBtnComponent } from "../../../components/ui-kit/buttons/primary-btn/primary-btn.component";
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AddPersistentWatcherComponent } from 'app/routes/core/watchers/components/add-persistent-watcher/add-persistent-watcher.component';
import { UserFlagsService } from 'app/services/user-flags.service';
import { take } from 'rxjs/operators';
import anime from 'animejs/lib/anime.es.js';
import mapboxgl from 'mapbox-gl';
import greatCircle from '@turf/great-circle';
import type { FeatureCollection, Point } from 'geojson';
import { uniqueId } from 'app/utils/crypto/unique-ids';
import { environment } from 'env/environment';
import { LocationService } from 'app/services/location.service';

@Component({
  selector: 'app-get-started',
  standalone: true,
  imports: [PageWrapperComponent, HeavyHeaderComponent, PrimaryBtnComponent, CommonModule, AddPersistentWatcherComponent],
  templateUrl: './get-started.component.html',
  styleUrl: './get-started.component.scss'
})
export class GetStartedComponent implements OnInit {
  slide: number = 1;
  progressBtnText: string = 'Next';

  @ViewChild('examplePill') examplePillRef!: ElementRef;
  exampleText: string = '';

  mapId = uniqueId('mapbox');
  map!: mapboxgl.Map;

  constructor(
    private router: Router,
    private userFlags: UserFlagsService,
    private locationService: LocationService
  ) { }

  ngOnInit(): void {
    this.userFlags.flags$.pipe(take(1)).subscribe(flags => {
      if (flags['hasCompletedOnboardEducation']) {
        this.router.navigate(['/agent']);
      }
    });
  }

  private examples: string[] = [
    '"My flight from Austin to Vail on the 16th has 4 open seats"',
    '"I want to go to Miami in the next week. I need two seats".',
    '"I want to go to the a beach in Mexico anytime this month"'
  ];

  ngAfterViewInit() {
    this.loopThroughStatements();
  }

  async onClickContinue() {
    ++this.slide;
    if (this.slide === 2) {
      await this.delay(250);
      this.getCords();
    }
    if (this.slide === 3) {
      this.progressBtnText = 'Get Started';
      await this.delay(250);
      this.animateSlideThree();
    }
    if (this.slide === 4) {
      this.progressBtnText = 'Finish';
    }
    if (this.slide === 5) {
      await this.completeOnboardEducation();
    }
  }

  async completeOnboardEducation() {
    await this.userFlags.setFlag('hasCompletedOnboardEducation');
    this.router.navigate(['/agent']);
  }

  private typeOutText(text: string, speed: number = 20): Promise<void> {
    return new Promise(resolve => {
      this.exampleText = '';
      let index = 0;

      const interval = setInterval(() => {
        this.exampleText += text.charAt(index);
        index++;

        if (index >= text.length) {
          clearInterval(interval);
          resolve();
        }
      }, speed);
    });
  }

  private animateIn(): Promise<void> {
    if (this.slide !== 1) {
      return Promise.resolve();
    } else {
      return anime({
        targets: this.examplePillRef.nativeElement,
        opacity: [0, 1],
        scale: [0.8, 1.5, 1],
        translateY: [14, 0],
        duration: 750,
        delay: 400,
        easing: 'easeOutQuad'
      }).finished;
    }
  }

  private animateOut(): Promise<void> {
    if (this.slide !== 1) {
      return Promise.resolve();
    } else {
      return anime({
        targets: this.examplePillRef.nativeElement,
        opacity: [1, 0],
        duration: 250,
        easing: 'easeInQuad'
      }).finished;
    }
  }

  private async loopThroughStatements() {
    let index = 0;

    while (this.slide === 1) {
      const statement = this.examples[index];
      await this.animateIn();
      await this.typeOutText(statement, 30);
      await this.delay(2200);
      await this.animateOut();
      this.exampleText = null;
      index = (index + 1) % this.examples.length;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // step 2 animation functions
  async getCords() {
    const originCords = await this.locationService.getCoordinates('Jackson Hole, WY, US');
    const destinationCords = await this.locationService.getCoordinates('Los Angeles, CA, US');

    if (originCords && destinationCords) {
      this.drawMap(originCords, destinationCords);
    }

  }

  drawMap(originCords, destinationCords) {
    mapboxgl.accessToken = environment.mapBoxToken;

    this.map = new mapboxgl.Map({
      container: this.mapId,
      style: 'mapbox://styles/p3ter185/cmcm3plwa00ae01s1f9ai1wbk',
      zoom: 0.5,
      dragPan: false
    });

    const origin: [number, number] = originCords;
    const destination: [number, number] = destinationCords;

    const line = greatCircle(origin, destination);
    this.map.on('load', () => {
      this.animateMap(origin, destination, line, originCords, destinationCords)
    });
  }

  animateMap(origin, destination, line, originCords, destinationCords) {
    this.map.getStyle().layers?.forEach(layer => {
      if (layer.id.includes('road')) {
        this.map.setLayoutProperty(layer.id, 'visibility', 'none');
      }
    });
    this.map.addSource('route', {
      type: 'geojson',
      data: line
    });


    this.map.addLayer({
      id: 'route',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#000000',
        'line-width': 2,
        'line-opacity': 1,
        'line-dasharray': [1, 3],
      }
    });

    const bounds = new mapboxgl.LngLatBounds();

    bounds.extend(originCords);
    bounds.extend(destinationCords);

    this.map.jumpTo({
      center: destination,
      zoom: 5
    });

    setTimeout(() => {
      this.map.fitBounds(bounds, {
        padding: 16,
        linear: true,
        duration: 1000
      });
      this.animateWatcherElements();
    }, 1000);

    const endpointsGeoJSON: FeatureCollection<Point> = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: origin
          },
          properties: { label: 'Origin' }
        },
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: destination
          },
          properties: { label: 'Destination' }
        }
      ]
    };

    this.map.addSource('endpoints', {
      type: 'geojson',
      data: endpointsGeoJSON
    });

    this.map.addLayer({
      id: 'endpoints',
      type: 'circle',
      source: 'endpoints',
      paint: {
        'circle-radius': 2,
        'circle-color': '#000000',
        'circle-stroke-width': 1,
        'circle-stroke-color': '#000000'
      }
    });


    this.map.dragPan.disable();
    this.map.scrollZoom.disable();
    this.map.boxZoom.disable();
    this.map.keyboard.disable();
    this.map.doubleClickZoom.disable();
    this.map.touchZoomRotate.disable();

  };

  animateWatcherElements() {
    anime({
      targets: '#location',
      opacity: [0, 1],
      duration: 250,
      easing: 'easeInQuad'
    });
    anime({
      targets: '#location-chip',
      opacity: [0, 1],
      duration: 250,
      easing: 'easeInQuad'
    });
  }

  animateSlideThree() {
    const timeline = anime.timeline({
      easing: 'easeOutQuad',
      autoplay: true,
      loop: true
    });

    timeline.add({
      targets: '#flight-shape',
      opacity: [0, 1],
      scale: [3, 1],
      translateY: [-20, 0],
      duration: 800,
      easing: 'spring(1, 80, 10, 0)'
    });

    // Animate #ripple first move
    timeline.add({
      targets: '#ripple',
      scale: [0.5, 0.7],
      opacity: [0.3, 0],
      duration: 3000,
      easing: 'linear'
    }, '-=900');

    // Animate #people-left and #people-right together
    timeline.add({
      targets: ['#people-left', '#people-right'],
      opacity: [0, 1],
      scale: [0.7, 1],
      translateY: [12, 0],
      duration: 2800,
      easing: 'spring(1, 80, 10, 0)',
    }, '-=2600');

    timeline.add({
      targets: ['#people-right'],
      translateX: [0, 20, -250],
      duration: 1500,
      easing: 'spring(1, 80, 10, 0)',
    }, 3000);

    timeline.add({
      targets: ['#people-left'],
      translateX: [0, -20, 250],
      duration: 1500,
      easing: 'spring(1, 80, 10, 0)',
    }, 3000);

    timeline.add({
      targets: ['#people-left', '#people-right'],
      opacity: [1, 0],
    });

    // Animate #tail-one-chip
    timeline.add({
      targets: '#tail-one-chip',
      scale: [0.8, 1.2],
      translateY: [0, -12],
      opacity: [0, 1],
      duration: 900,
      easing: 'spring(1, 80, 10, 0)',
    }, '-=1400');

    // Animate #ripple
    timeline.add({
      targets: ['#message-one', '#message-two'],
      opacity: [0, 1],
      duration: 800,
      translateY: [18, 0],
      delay: anime.stagger(200),
      easing: 'spring(1, 80, 10, 0)',
    }, '-=800');

    timeline.add({
      translateY: [0, -12],
      duration: 50,
      easing: 'linear'
    }, '-=1900');

    timeline.add({
      targets: '#ripple',
      scale: [0, 1.3],
      opacity: [1, 0],
      duration: 1800,
      easing: 'easeInExpo'
    }, '-=3000');

    timeline.add({
      duration: 6000,
    });
  }
}
